/**
 * dexAnchorDetector (PR4 — device-relative ROIs).
 *
 * Why this module exists
 * ----------------------
 * Every dex OCR ROI used to be a FIXED fraction of the frame:
 *   - dexOcrRoi.GRID_BOTTOM = 0.79      (grid bottom anchor)
 *   - pokedexOcr.FILTER_PILL_ROW_Y = 0.85 (variant-pill band)
 * These were calibrated to one 19.5:9 phone. On iPad (4:3), 16:9 devices, and
 * Dynamic-Island phones the real grid bottom and the real filter-pill row sit
 * at DIFFERENT fractions, so the whole ROI stack drifts off-target — the OCR
 * worst-case weak point ("weak point #1").
 *
 * This module derives those anchors PER FRAME from the captured pixels, with
 * the old constants as fall-back when detection is not confident (so no device
 * can regress relative to today's behaviour).
 *
 * Two layered detectors (Option C):
 *
 *   1. detectGridBottomFrac  — PRIMARY, theme-independent + occlusion-proof.
 *      The Pokémon GO dex grid is a vertically-periodic lattice: high "content
 *      energy" cell rows (sprites / numbers / card fills) separated by low-
 *      energy gutters (flat tinted background). We scan a per-row energy
 *      profile over the grid-width band, find the row pitch by autocorrelation,
 *      and report the bottom edge of the lowest detected cell row. The grid
 *      body is NEVER occluded by the in-app PiP (which floats over the bottom
 *      band, below the grid), so this is the robust anchor.
 *
 *   2. detectFilterPillRowFrac — SECONDARY, constrained by (1). Once the grid
 *      bottom is known, the filter-pill row is a short search window just below
 *      it: the bottommost horizontal band containing a single saturated capsule
 *      (the active pill) flanked by near-white outlined chips. PiP can occlude
 *      it; when it does we return low confidence and the caller falls back to
 *      the fixed FILTER_PILL_ROW_Y.
 *
 * Pure module. No DOM. Operates on a full-frame RGBA buffer (the caller already
 * builds one for detectFastScrollPill, so reuse is free). All outputs are
 * fractions of frame height (0..1), x→right, y→down.
 */

/** Result of a per-frame anchor detection. */
export interface AnchorDetection {
  /** Detected fraction (0..1) of frame height. */
  frac: number;
  /**
   * 0..1 confidence. Callers gate on a threshold and fall back to the fixed
   * constant below it. Confidence reflects how cleanly the periodic / capsule
   * signal stood out from the background.
   */
  confidence: number;
}

/**
 * Minimum confidence at/above which a caller should trust a detected anchor
 * over the fixed-fraction fallback. Calibrated so that a clean periodic grid
 * (autocorrelation peak well above the flat baseline) trusts the detector,
 * while a noisy / partially-captured frame falls back.
 */
export const ANCHOR_TRUST_CONFIDENCE = 0.45;

/** Horizontal grid extent we sample (matches dexOcrRoi.GRID_LEFT/RIGHT). */
const SAMPLE_LEFT = 0.04;
const SAMPLE_RIGHT = 0.96;

/**
 * Per-row content-energy profile over the grid-width band. Energy at row y is
 * the mean absolute vertical luminance gradient |L(y) - L(y-1)| averaged over
 * the sampled columns. Cell rows (sprites, dex numbers, card borders) carry
 * high energy; inter-row gutters of flat tinted background carry low energy.
 *
 * Returns a Float32Array of length `height` (row 0 = top). row 0 is forced to
 * the row-1 value so the array has no spurious leading zero.
 */
export function rowEnergyProfile(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): Float32Array {
  const xL = Math.max(0, Math.floor(width * SAMPLE_LEFT));
  const xR = Math.min(width, Math.ceil(width * SAMPLE_RIGHT));
  const cols = Math.max(1, xR - xL);

  // Mean luminance per row.
  const rowLum = new Float32Array(height);
  for (let y = 0; y < height; y++) {
    let sum = 0;
    const base = y * width;
    for (let x = xL; x < xR; x++) {
      const i = (base + x) * 4;
      sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    }
    rowLum[y] = sum / cols;
  }

  // Vertical gradient magnitude per row.
  const energy = new Float32Array(height);
  for (let y = 1; y < height; y++) {
    energy[y] = Math.abs(rowLum[y] - rowLum[y - 1]);
  }
  if (height > 1) energy[0] = energy[1];
  return energy;
}

/**
 * Detect the grid bottom edge as a fraction of frame height.
 *
 * Strategy: over the vertical band the grid can occupy (below the header, above
 * the very bottom nav), find the dominant vertical period of the energy profile
 * by autocorrelation, then locate the lowest energy PEAK consistent with that
 * period — its lower boundary is the grid bottom.
 *
 *   - searchTopFrac / searchBotFrac bound where we look (defaults below cover
 *     every observed device: grid never starts above ~0.18 nor ends below
 *     ~0.80; the filter pills + nav live below 0.80).
 *
 * Confidence = normalised autocorrelation peak prominence. Returns frac clamped
 * to the search band. When the signal is flat (no periodic grid — e.g. a non-
 * dex frame), confidence is ~0 and the caller falls back.
 */
export function detectGridBottomFrac(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  searchTopFrac = 0.18,
  searchBotFrac = 0.82,
  pipActive = false,
  /**
   * The Nexus HUD card's rows (frame fractions), zeroed out of the profile
   * the way the dark-slab pass below zeroes the old dark card. The pale card
   * never trips the luminance test, and its top and bottom edges inside the
   * search band cost the periodicity 0.1-0.35 of confidence on the device
   * frames (ad4133e6: 0.37 with the card, 0.74 without), enough to drop the
   * pill-row detection back to its fixed fallback.
   */
  overlayRows: { y: number; h: number } | null = null,
  /**
   * Row-pitch window for the autocorrelation, fractions of the height. The
   * default is the phone window the detector was tuned on; a 4:3 tablet's
   * rows are 0.217 of its height (lib/dexLayout), outside it.
   */
  pitchSearch: { min: number; max: number } = { min: 0.08, max: 0.16 },
): AnchorDetection {
  if (width < 8 || height < 40) return { frac: 0.66, confidence: 0 };

  const energy = rowEnergyProfile(data, width, height);
  if (overlayRows) {
    const m = Math.max(12, Math.floor(height * 0.012));
    const a = Math.max(0, Math.floor(overlayRows.y * height) - m);
    const b = Math.min(height - 1, Math.ceil((overlayRows.y + overlayRows.h) * height) + m);
    for (let y = a; y <= b; y++) energy[y] = 0;
  }

  const top = Math.max(1, Math.floor(height * searchTopFrac));
  const bot = Math.min(height - 1, Math.ceil(height * searchBotFrac));
  if (bot - top < 20) return { frac: 0.66, confidence: 0 };

  // PiP-mask (b18 — Issues 2+3 root fix): the in-app PiP "Scanning…" overlay is
  // a DARK card (lum ~90-110) that floats over the capture. Empirically it can
  // sit LOW (yfrac ~0.78+ on the XXS device test, IMG_0901), not only top-right
  // as the placement policy assumes. Its high-contrast top edge is the strongest
  // energy peak in the band, so the unmasked detector locks onto the PiP shoulder
  // (frac ~0.82) instead of the true grid bottom (~0.66). When PiP is active we
  // locate the contiguous dark overlay slab by luminance and zero its energy so
  // no grid-bottom peak can form there. Corner-robust: the slab is dark wherever
  // it snaps. The grid body itself is bright (lum ~180+), so a real grid row is
  // never mistaken for the overlay.
  if (pipActive) {
    const xL = Math.max(0, Math.floor(width * SAMPLE_LEFT));
    const xR = Math.min(width, Math.ceil(width * SAMPLE_RIGHT));
    const cols = Math.max(1, xR - xL);
    const rowLum = new Float32Array(height);
    for (let y = top; y <= bot; y++) {
      let sum = 0;
      const base = y * width;
      for (let x = xL; x < xR; x++) {
        const i = (base + x) * 4;
        sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      }
      rowLum[y] = sum / cols;
    }
    // Robust grid-band luminance baseline: median of the band.
    const sorted = Array.from(rowLum.subarray(top, bot + 1)).sort((a, b) => a - b);
    const medLum = sorted[Math.floor(sorted.length / 2)] || 0;
    // A row belongs to the dark overlay slab when it is markedly darker than the
    // grid background. 0.62 * median ≈ 110 when the grid sits at ~180 — well
    // below any tinted grid gutter, comfortably above the PiP card (~90-110).
    const darkThresh = medLum * 0.62;
    // Mark every dark-overlay row, then zero each dark row PLUS a margin on both
    // sides. The margin MUST exceed the smoothing radius (k ≈ height*0.004) plus
    // the luminance transition width, otherwise the box-filter smears the slabs
    // high-contrast edges back into the band and a phantom peak survives right at
    // the overlay boundary (~0.80). A symmetric bounded margin (rather than
    // "zero everything below the slab") keeps this corner-safe: a TOP-corner PiP
    // is masked locally without wiping the grid rows beneath it.
    const slabMargin = Math.max(12, Math.floor(height * 0.012));
    const dark: boolean[] = new Array(height).fill(false);
    for (let y = top; y <= bot; y++) {
      if (rowLum[y] < darkThresh) dark[y] = true;
    }
    for (let y = top; y <= bot; y++) {
      if (dark[y]) {
        const lo = Math.max(top, y - slabMargin);
        const hi = Math.min(bot, y + slabMargin);
        for (let yy = lo; yy <= hi; yy++) energy[yy] = 0;
      }
    }
  }

  // Smooth the energy with a small box filter to suppress single-pixel noise.
  const k = Math.max(2, Math.floor(height * 0.004));
  const sm = new Float32Array(height);
  for (let y = top; y <= bot; y++) {
    let s = 0;
    let n = 0;
    for (let d = -k; d <= k; d++) {
      const yy = y + d;
      if (yy >= top && yy <= bot) {
        s += energy[yy];
        n++;
      }
    }
    sm[y] = s / Math.max(1, n);
  }

  // Mean + max over the band (for baseline + normalisation).
  let mean = 0;
  let maxE = 0;
  for (let y = top; y <= bot; y++) {
    mean += sm[y];
    if (sm[y] > maxE) maxE = sm[y];
  }
  mean /= Math.max(1, bot - top + 1);
  if (maxE <= 1e-6) return { frac: 0.66, confidence: 0 };

  // Autocorrelation over a plausible row-pitch range (8%..16% of frame height;
  // observed ROW_HEIGHT 0.14625, dexOcrRoi). Find the lag with the highest correlation.
  const lagMin = Math.max(4, Math.floor(height * pitchSearch.min));
  const lagMax = Math.max(lagMin + 1, Math.floor(height * pitchSearch.max));
  let bestLag = lagMin;
  let bestCorr = -Infinity;
  for (let lag = lagMin; lag <= lagMax; lag++) {
    let corr = 0;
    let cnt = 0;
    for (let y = top; y + lag <= bot; y++) {
      corr += (sm[y] - mean) * (sm[y + lag] - mean);
      cnt++;
    }
    if (cnt > 0) {
      corr /= cnt;
      if (corr > bestCorr) {
        bestCorr = corr;
        bestLag = lag;
      }
    }
  }

  // Variance for normalising the autocorrelation peak into 0..1 confidence.
  let variance = 0;
  for (let y = top; y <= bot; y++) variance += (sm[y] - mean) * (sm[y] - mean);
  variance /= Math.max(1, bot - top + 1);
  const periodicity = variance > 1e-6 ? Math.max(0, bestCorr / variance) : 0;

  // Find energy peaks (local maxima above the mean) within the band, then take
  // the LOWEST peak that fits the detected pitch. The grid bottom is just below
  // that peak's lower shoulder.
  const peakThresh = mean + 0.35 * (maxE - mean);
  let lowestPeakY = -1;
  for (let y = top + 1; y < bot; y++) {
    if (sm[y] >= peakThresh && sm[y] >= sm[y - 1] && sm[y] >= sm[y + 1]) {
      lowestPeakY = y;
    }
  }
  if (lowestPeakY < 0) return { frac: 0.66, confidence: 0 };

  // Walk down from the lowest peak to its lower shoulder (energy falls back to
  // ~baseline): that boundary is the grid bottom.
  let shoulder = lowestPeakY;
  const shoulderFloor = mean + 0.15 * (maxE - mean);
  while (shoulder < bot && sm[shoulder + 1] > shoulderFloor) shoulder++;
  // Add half a pitch below the last peak only if the shoulder did not already
  // extend there — keeps the bottom at the true grid edge, not mid-gutter.
  const frac = Math.min(searchBotFrac, shoulder / height);

  // Confidence blends periodicity with how far the lowest peak stood above the
  // baseline.
  const peakProminence = (sm[lowestPeakY] - mean) / Math.max(1e-6, maxE - mean);
  const confidence = Math.max(0, Math.min(1, 0.6 * periodicity + 0.4 * peakProminence));

  return { frac, confidence };
}

/** Fractional bounding rectangle (0..1 of frame width/height), x->right, y->down. */
export interface FrameRectFrac {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Detect the in-app PiP overlay's bounding rectangle, corner-agnostically.
 *
 * Why this exists
 * ---------------
 * The PiP number-ROI mask in dexOcrRoi (DEFAULT_EXCLUSION_ZONES.pip) was a FIXED
 * top-right rectangle. But the PoGo/Nex PiP is a native iOS PiP window the user
 * drags ANYWHERE, and the domain rule forces it off the variant-tag corners, so
 * in practice it sits bottom-left OR bottom-right (it cannot lock to one corner
 * or it would hide a first-dex / perfect-dex tag). When the PiP is bottom-left
 * the fixed top-right mask covers empty header space, and the PiP's "#001"/
 * "Added" text floating over a real grid row gets OCR'd as a cell number ->
 * phantom catch. iOS owns the window position and the diagnostics block exposes
 * no rect, so the position is not known from app state; it must be detected.
 *
 * The PiP is a DARK rounded card (lum ~90-110) that floats over the brighter
 * grid (lum ~180+). detectGridBottomFrac already locates the dark slab BY ROW to
 * zero its energy (corner-robust). This function reuses the same luminance test
 * to find the slab's vertical extent AND its horizontal extent, returning a
 * fractional rect the OCR mask can subtract wherever the user parked the PiP.
 *
 * Returns null when no contiguous dark slab is found (PiP closed / not over the
 * grid) so the caller keeps its fixed-rect fallback and cannot regress.
 *
 * Pure module; no DOM. Operates on the same full-frame RGBA buffer.
 */
export function detectPipSlabRect(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  searchTopFrac = 0.18,
  searchBotFrac = 0.98,
): FrameRectFrac | null {
  if (width < 8 || height < 40) return null;
  const top = Math.max(1, Math.floor(height * searchTopFrac));
  const bot = Math.min(height - 1, Math.ceil(height * searchBotFrac));
  if (bot - top < 12) return null;

  // A corner-parked PiP card occupies only PART of the row width, so a full-width
  // row MEAN would be diluted by the bright grid beside it and never read dark.
  // Instead, classify a row as "dark" when it contains a sufficiently WIDE
  // contiguous run of dark pixels (the card body). The dark threshold is an
  // absolute luminance: the PiP card is ~90-110, the grid body ~180+, so 140
  // cleanly separates them and needs no per-row median.
  const DARK_LUM = 140;
  // The card must be at least this wide (fraction of frame width) to count as a
  // PiP, so a thin dark UI line or a tinted gutter column never qualifies.
  const MIN_RUN_PX = Math.max(8, Math.floor(width * 0.12));
  const rowDark = new Array<boolean>(height).fill(false);
  for (let y = top; y <= bot; y++) {
    const base = y * width;
    let run = 0;
    let bestRun = 0;
    for (let x = 0; x < width; x++) {
      const i = (base + x) * 4;
      const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      if (lum < DARK_LUM) {
        run++;
        if (run > bestRun) bestRun = run;
      } else {
        run = 0;
      }
    }
    rowDark[y] = bestRun >= MIN_RUN_PX;
  }
  const darkThresh = DARK_LUM;

  // Find the LARGEST contiguous run of dark rows (gap tolerance so a faint
  // text line inside the card doesn't split the slab).
  let bestTop = -1;
  let bestBot = -1;
  let curTop = -1;
  let gap = 0;
  const GAP_TOL = Math.max(2, Math.floor(height * 0.004));
  for (let y = top; y <= bot; y++) {
    if (rowDark[y]) {
      if (curTop < 0) curTop = y;
      gap = 0;
    } else if (curTop >= 0) {
      gap++;
      if (gap > GAP_TOL) {
        const runBot = y - gap;
        if (bestTop < 0 || runBot - curTop > bestBot - bestTop) {
          bestTop = curTop;
          bestBot = runBot;
        }
        curTop = -1;
        gap = 0;
      }
    }
  }
  if (curTop >= 0) {
    const runBot = bot;
    if (bestTop < 0 || runBot - curTop > bestBot - bestTop) {
      bestTop = curTop;
      bestBot = runBot;
    }
  }
  if (bestTop < 0) return null;
  // A real PiP card spans several rows; a 1-2px dark line is noise.
  const slabHeightPx = bestBot - bestTop + 1;
  if (slabHeightPx < Math.max(8, Math.floor(height * 0.03))) return null;

  // Horizontal extent: within the slab rows, find the min/max x of dark pixels
  // across the FULL width (not just the sampled band) so the rect captures the
  // card wherever it snapped left or right.
  let minX = width;
  let maxX = -1;
  for (let y = bestTop; y <= bestBot; y++) {
    const base = y * width;
    for (let x = 0; x < width; x++) {
      const i = (base + x) * 4;
      const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      if (lum < darkThresh) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
      }
    }
  }
  if (maxX < minX) return null;
  const slabWidthPx = maxX - minX + 1;
  // The PiP is a compact card, never a full-width band; reject a near-full-width
  // dark run (that would be a dark theme gutter, not the PiP).
  if (slabWidthPx > width * 0.96) return null;

  return {
    x: minX / width,
    y: bestTop / height,
    w: slabWidthPx / width,
    h: slabHeightPx / height,
  };
}

/** HSV-ish saturation of an RGB triple (0..1). */
function satOf(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max > 1e-6 ? (max - min) / max : 0;
}

/**
 * Detect the variant-pill row centre as a fraction of frame height, searching a
 * window below the (already detected) grid bottom.
 *
 * The variant pills (LUCKY / XXL / XXS / SHINY) are WIDE pale rounded capsules
 * with dark text strokes inside, sitting at the very bottom of the dex UI
 * (y ≈ 0.86–0.90 on real device frames). Directly ABOVE them sits the row of
 * circular nav buttons (search / X / REGIONS) + grid numbers at y ≈ 0.81. Both
 * rows contain pale pixels, so a naive "highest contrast" or "most pale pixels"
 * score locks onto the wrong (button) band on most frames.
 *
 * The discriminating signal is HORIZONTAL EXTENT: the pill capsules are wide and
 * their pale fill is SUSTAINED across a large, contiguous fraction of the row
 * width, whereas the circular buttons are narrow and localized (3 small pale
 * clusters with long gaps between them). We therefore score each row by:
 *   - the widest horizontal SPAN of pale-capsule pixels (low saturation,
 *     lum > ~0.78), measured as the run between the left-most and right-most pale
 *     pixel, weighted by how densely that span is filled with pale pixels, AND
 *   - the presence of dark text strokes INSIDE that pale span (the pill labels).
 * A row of narrow buttons has a wide span but sparse fill (long gaps), so it
 * scores below the densely-filled wide pill row.
 *
 * Ties break toward the LOWER (larger y) band: the pills are always below the
 * button/REGIONS row. When the score is weak the confidence is low and the
 * caller falls back to FILTER_PILL_ROW_Y.
 */
export function detectFilterPillRowFrac(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  gridBottomFrac: number,
  /**
   * Where the pills can sit, fractions of the height. The default is the
   * phone window below; a 4:3 tablet draws them at 0.748-0.826 (lib/dexLayout).
   */
  window: { top: number; bottom: number } = { top: 0.8, bottom: 0.9 },
): AnchorDetection {
  if (width < 8 || height < 40) return { frac: 0.85, confidence: 0 };

  // Search window 0.80-0.90 (2026-09-24 device screenshots, real Vision
  // harness). In current Pokémon GO the filter pills sit at ~0.84-0.88 and the
  // round search / X / REGIONS buttons BELOW them at ~0.905-0.96. The old
  // window ran to 0.95 and the tie-break below prefers the lower row, so on the
  // Perfect page (one white pill: 100% is coral, nothing right of it) and on
  // pale mint pages the band landed on the buttons and the pill read came back
  // empty every frame. The 0.80 floor keeps a pale page's grid cards out.
  const top = Math.min(height - 2, Math.max(1, Math.floor(height * Math.max(window.top, gridBottomFrac + 0.02))));
  const bot = Math.min(height - 1, Math.floor(height * window.bottom));
  if (bot - top < 8) return { frac: 0.85, confidence: 0 };

  const xL = Math.max(0, Math.floor(width * 0.04));
  const xR = Math.min(width, Math.ceil(width * 0.96));
  const sampleW = Math.max(1, xR - xL);

  let bestY = -1;
  let bestScore = -Infinity;
  const rowScores = new Float32Array(bot - top + 1);
  for (let y = top; y <= bot; y++) {
    const base = y * width;
    let firstPale = -1; // x of left-most pale-capsule pixel
    let lastPale = -1; // x of right-most pale-capsule pixel
    let paleCount = 0; // pale-capsule pixels in the row
    let darkCount = 0; // dark text-stroke pixels in the row
    for (let x = xL; x < xR; x++) {
      const i = (base + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const s = satOf(r, g, b);
      const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      // Pale capsule body: low saturation, high luminance.
      if (s < 0.18 && lum > 0.78) {
        if (firstPale < 0) firstPale = x;
        lastPale = x;
        paleCount++;
      } else if (lum < 0.45) {
        // Dark text stroke (the pill label glyphs).
        darkCount++;
      }
    }
    if (firstPale < 0) continue;
    // Horizontal span of pale pixels (wide for capsules, also wide for the
    // 3-button row) and how densely that span is filled (HIGH for the sustained
    // pill capsules, LOW for narrow buttons separated by long gaps).
    const spanFrac = (lastPale - firstPale + 1) / sampleW;
    const paleFrac = paleCount / sampleW;
    // Fill density within the span discriminates wide capsules (dense) from
    // narrow buttons (sparse — long empty gaps drop the density).
    const spanPx = lastPale - firstPale + 1;
    const fillDensity = spanPx > 0 ? paleCount / spanPx : 0;
    // Dark text contrast inside the pale region (the variant label).
    const textFrac = darkCount / sampleW;
    // Reward a WIDE, DENSELY-filled pale span (the capsule body) that also
    // carries interior dark text. spanFrac*paleFrac favours sustained coverage;
    // fillDensity penalizes the sparse 3-button row; the +text term requires the
    // label glyphs so a blank pale strip does not win.
    const score =
      spanFrac * paleFrac * (0.4 + 0.6 * fillDensity) * (0.5 + Math.min(1, textFrac * 6));
    // Prefer the LOWER band on (near-)ties: pills are below the button row. A
    // strict > would keep the first (higher) of two equal rows; the margin below
    // lets a marginally weaker-but-lower row (the pills, vs the button row just
    // above) win, since rows are scanned top→bottom.
    const tieFloor = bestScore > 0 ? bestScore * 0.96 : bestScore;
    rowScores[y - top] = score;
    if (score >= tieFloor) {
      if (score > bestScore) bestScore = score;
      bestY = y;
    }
  }

  if (bestY < 0) return { frac: 0.85, confidence: 0 };
  // The landmark is the CENTRE of the capsule band, not the single best row:
  // on the Galaxy (2026-09-25 harness) the best row was the pills' top edge on
  // two frames and their label row on three, which moved the bottom-UI fence
  // by half a card and skipped four visible cards per frame. Every row that
  // scores at least half the best is capsule; the band is their extent.
  let topQ = bestY;
  let botQ = bestY;
  const floor = bestScore * 0.5;
  for (let y = bestY; y >= top && rowScores[y - top] >= floor; y--) topQ = y;
  for (let y = bestY; y <= bot && rowScores[y - top] >= floor; y++) botQ = y;
  const frac = (topQ + botQ) / 2 / height;
  // Confidence from the absolute strength of the pill-capsule signal, scaled so
  // a clean wide-capsule row clears ANCHOR_TRUST_CONFIDENCE while a marginal
  // sliver does not.
  const confidence = Math.max(0, Math.min(1, bestScore * 3));
  return { frac, confidence };
}
